<template>
  <v-navigation-drawer rail permanent :color="drawerColor" border="0" :rail-width="74">
    <div
      class="pa-2 pb-3 d-flex align-center justify-center item-clickable"
      @click="handleLogoClick()"
    >
      <img v-if="showBrandedIcon" :alt="logoAlt" :src="appLogo" height="36" />
      <v-img
        v-else
        :alt="logoAlt"
        src="/images/logos/coro-logo-small.svg"
        width="36"
        height="36"
        contain
      />
    </div>
    <v-divider color="white" />

    <div class="d-flex flex-column justify-space-between">
      <v-list v-if="workspace.workspaceId" :lines="false" data-testid="navigation-menu">
        <template v-for="item in menuButtons" :key="item.id">
          <v-divider v-if="item.type === 'divider'" color="white" class="mt-2 mb-3 mx-2" />
          <v-list-item
            v-else-if="item.type === 'link'"
            class="mb-3 px-1"
            :active="isNavigationItemActive(item)"
            color="primary"
            active-class="menu-button--active"
            min-height="24"
            max-height="56"
            width="74"
            height="56"
            :ripple="false"
            variant="plain"
            :data-testid="`navigation-menu-item-${item.id}`"
            @click="navigateToMenuItem(item.routeName, item.text)"
          >
            <template #default>
              <div class="d-flex align-center justify-center flex-column">
                <div class="icon-wrapper">
                  <v-icon class="menu-icon" :icon="item.icon" />
                </div>
                <div class="small-text">
                  {{ item.text }}
                </div>
              </div>
            </template>
          </v-list-item>
          <v-menu
            v-else-if="item.type === 'submenu'"
            @update:model-value="handleListsMenu"
            ref="submenu"
            open-on-click
            location="end"
            :offset="5"
            close-on-content-click
            :data-testid="`navigation-submenu-${item.id}`"
          >
            <template #activator="{ props }">
              <v-list-item
                class="mb-3 px-1"
                :active="isNavigationItemActive(item)"
                color="primary"
                active-class="menu-button--active"
                min-height="24"
                max-height="56"
                width="74"
                height="56"
                v-bind="props"
                :ripple="false"
                variant="plain"
                :data-testid="`navigation-submenu-activator-${item.id}`"
              >
                <template #default>
                  <div class="d-flex align-center justify-center flex-column">
                    <div class="icon-wrapper">
                      <v-icon class="menu-icon" :icon="item.icon" />
                    </div>
                    <div class="small-text">
                      {{ item.text }}
                    </div>
                  </div>
                </template>
              </v-list-item>
            </template>

            <v-list :data-testid="`navigation-submenu-list-${item.id}`">
              <template v-for="option in item.items" :key="option.id">
                <v-divider v-if="option.type === 'divider'" />
                <v-list-subheader v-else-if="option.type === 'subheader'" :title="option.text" />
                <v-list-item
                  v-else
                  @click="navigateToMenuItem(option.routeName, option.text)"
                  :active="isRouteActive(option.routeName)"
                  :data-testid="`navigation-submenu-item-${option.id}`"
                >
                  <v-list-item-title>
                    {{ option.text }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </template>
      </v-list>
    </div>

    <template v-slot:append>
      <v-list v-if="workspace.workspaceId" :lines="false" data-testid="navigation-append-menu">
        <v-list-item
          class="mb-3 px-1"
          :active="isRouteActive(RouteName.ALERTS)"
          @click="navigateToMessages()"
          color="primary"
          active-class="menu-button--active"
          min-height="24"
          max-height="56"
          width="74"
          height="56"
          variant="plain"
          :ripple="false"
          data-testid="navigation-alerts-button"
        >
          <template #default>
            <div class="d-flex align-center justify-center flex-column">
              <div class="icon-wrapper">
                <v-icon class="menu-icon" icon="$bell"> </v-icon>
              </div>
              <div class="small-text">
                {{ $t("general.messages") }}
              </div>
            </div>
          </template>
        </v-list-item>
      </v-list>

      <v-menu
        :open-on-hover="false"
        close-on-content-click
        close-on-back
        min-width="320"
        location="end"
        data-testid="navigation-account-menu"
      >
        <template #activator="{ props }">
          <div class="d-flex w-100 justify-center align-center mb-4">
            <v-avatar
              role="button"
              v-bind="props"
              :size="36"
              color="indigo-light"
              class="subtitle2"
              :text="initials"
              data-testid="navigation-account-button"
            />
          </div>
        </template>
        <div class="px-4 pt-4 rounded bg-white elevation-4">
          <div class="d-flex ml-4">
            <v-avatar :size="40" color="indigo-light" class="subtitle2 mr-4" :text="initials" />
            <div class="d-flex flex-column">
              <div class="subtitle2 word-break-all" v-if="nameInfo.firstName && nameInfo.lastName">
                {{ `${nameInfo.firstName} ${nameInfo.lastName}` }}
              </div>
              <div class="body2 word-break-all">
                {{ nameInfo.email }}
              </div>
            </div>
          </div>

          <v-list density="compact" data-testid="navigation-account-menu-list">
            <v-list-item v-if="showSetupHubItem" @click="restoreOnboarding()">
              <v-list-item-title class="text-capitalize">
                {{ $t("appBar.userContextMenu.setupHub") }}
              </v-list-item-title>
            </v-list-item>
            <div
              :data-testid="`account-menu-item-${i}`"
              v-for="(item, i) in userContextMenu"
              :key="i"
            >
              <v-list-item
                v-if="item.type === 'link'"
                @click="handleLinkContextMenuItemNavigation(item)"
              >
                <v-list-item-title class="text-capitalize">
                  {{ $t(item.titleKey) }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-else-if="item.type === 'callback'" @click="item.callback()">
                <v-list-item-title class="text-capitalize">
                  {{ $t(item.titleKey) }}
                </v-list-item-title>
              </v-list-item>
              <v-divider v-else class="my-2" />
            </div>
          </v-list>
        </div>
      </v-menu>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useMyAccountStore } from "@/_store/my-account.module";
import { i18n } from "@/plugins/i18n";
import { isGlobalAccessRestricted, isGlobalActionRestricted } from "@/_helpers/global-permissions";
import {
  GlobalRoleScopes,
  GlobalRoleScopeSection,
  MspPortalScopeSection,
  MspRolePermissionScopes,
  RolePermissionsScope,
} from "@/_store/roles.module";
import { SubscriptionModule } from "@/constants/workplaces";
import { isWorkspaceFrozen, isWorkspaceInitial, trackGtmEvent } from "@/_helpers/utils";
import { RouteName } from "@/constants/routes";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { isMspAccessRestricted } from "@/_helpers/msp-permissions.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import { appLogoDefaultPath, useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useSetupHubStore } from "@/_store/setup-hub.module.ts";

export type ContextMenuItem =
  | {
      type: "link";
      titleKey: string;
      routeName: RouteName;
    }
  | {
      type: "callback";
      titleKey: string;
      callback: () => void;
    }
  | {
      type: "divider";
    };

type NameInformation = {
  firstName?: string;
  lastName?: string;
  email: string;
};

export type NavigationLinkItem = {
  id: string;
  type: "link";
  routeName: RouteName;
  icon: string;
  text: string;
};

type NavigationDividerItem = {
  id: string;
  type: "divider";
};

export type SubMenuButtonItem = {
  id: string;
  type: "button";
  routeName: RouteName;
  text: string;
};

type SubMenuDividerItem = {
  id: string;
  type: "divider";
};

type SubMenuSubheaderItem = {
  id: string;
  type: "subheader";
  text: string;
};

export type SubMenuItem = SubMenuButtonItem | SubMenuDividerItem | SubMenuSubheaderItem;

export type NavigationSubMenuItem = {
  id: string;
  type: "submenu";
  icon: string;
  text: string;
  items: SubMenuItem[];
};

export type NavigationItem = NavigationLinkItem | NavigationDividerItem | NavigationSubMenuItem;

export default defineComponent({
  props: {
    isMspRoute: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const accountStore = useAccountStore();
    const myAccountStore = useMyAccountStore();
    const workspaceStore = useWorkspaceStore();
    const setupHubStore = useSetupHubStore();
    const route = useRoute();
    const router = useRouter();
    const menuOpened = ref(false);
    const { workspace, appLogo } = storeToRefs(workspaceStore);
    const { restoreOnboarding } = setupHubStore;
    const isMFASetupInProgress = computed(() => route.name === RouteName.MFA_PAGE);

    const nameInfo = computed<NameInformation>(() => {
      const { firstName, lastName } = myAccountStore.myAccount.profileData;
      const email = accountStore.email;
      return { firstName, lastName, email };
    });

    const initials = computed(() => {
      const { firstName, lastName, email } = nameInfo.value;
      if (firstName && lastName) {
        return firstName.substring(0, 1).toUpperCase() + lastName.substring(0, 1).toUpperCase();
      }

      return email.substring(0, 2).toUpperCase();
    });

    const showSetupHubItem = computed(() => {
      return setupHubStore.data?.dismissed;
    });

    const backOfficeEnabled = computed(
      () =>
        !isGlobalActionRestricted(
          GlobalRoleScopes.GLOBAL_SCOPE,
          GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
          "backOffice"
        )
    );
    const socTicketLogEnabled = computed(
      () =>
        !isGlobalAccessRestricted(GlobalRoleScopes.GLOBAL_SCOPE, GlobalRoleScopeSection.SOC_PORTAL)
    );
    const securityGapsButtonEnabled = computed(() => {
      return !(isWorkspaceFrozen() || isWorkspaceInitial());
    });

    const mspTicketsButtonEnabled = computed(() => {
      return (
        [
          SubscriptionModule.USER_DATA_GOVERNANCE,
          SubscriptionModule.EMAIL_SECURITY,
          SubscriptionModule.CLOUD_SECURITY,
          SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
          SubscriptionModule.ENDPOINT_SECURITY,
          SubscriptionModule.EDR,
        ] as const
      ).some((module) => !isMspAccessRestricted(RolePermissionsScope.TICKETS, module));
    });

    const globalWorkspacesButtonEnabled = computed(() => {
      return !isGlobalAccessRestricted(
        MspRolePermissionScopes.MSP_PORTAL_SCOPE,
        MspPortalScopeSection.MSP_WORKSPACES
      );
    });

    const showBrandedIcon = computed(() => appLogo.value !== appLogoDefaultPath);
    const logoAlt = computed(() => workspace.value.name || i18n.global.t("services.coronet"));

    /**
     * Lightens a given hex color by a specified amount.
     * @param hex - The hex color code (e.g., "#RRGGBB" or "#RGB").
     * @param percent - The percentage to lighten the color by (0-100).
     * @returns The new, lighter hex color code as a string.
     */
    const lightenHexColor = (hex: string, percent: number): string => {
      // 1. Remove the '#' and normalize to a 6-digit hex code
      let color = hex.startsWith("#") ? hex.slice(1) : hex;
      if (color.length === 3) {
        color = color
          .split("")
          .map((char) => char + char)
          .join("");
      }

      // 2. Convert hex to RGB integer values
      const num = parseInt(color, 16);
      let r = num >> 16;
      let g = (num >> 8) & 0x00ff;
      let b = num & 0x0000ff;

      // 3. Calculate the lighter RGB values
      const amount = percent / 100;
      r = Math.round(Math.min(255, r + (255 - r) * amount));
      g = Math.round(Math.min(255, g + (255 - g) * amount));
      b = Math.round(Math.min(255, b + (255 - b) * amount));

      // 4. Convert the new RGB values back to a hex string
      const toHex = (c: number): string => ("00" + c.toString(16)).slice(-2);
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const drawerColor = computed(() =>
      workspace.value.branding?.brandColor ? workspace.value.branding.brandColor : "primary"
    );

    const activeIconWrapperColor = computed<string>(() => {
      const color = drawerColor.value;

      if (color === "primary") {
        // If the color is the default "primary", use the specific variable
        return "#5853b1";
      } else {
        // Otherwise, it's a custom hex color. Lighten it by 20%.
        return lightenHexColor(color, 20);
      }
    });

    const showGlobalSettings = () => {
      const globalSections = [
        MspPortalScopeSection.MSP_ROLES,
        MspPortalScopeSection.MSP_ADMIN_USERS,
        MspPortalScopeSection.MSP_WORKSPACE_LABELS,
        MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS,
      ];

      return globalSections.some(
        (section) => !isMspAccessRestricted(MspRolePermissionScopes.MSP_PORTAL_SCOPE, section)
      );
    };

    const buildGlobalSubmenu = (
      prefix: string,
      routes: { backOffice: RouteName; socTickets: RouteName; globalWorkspaces: RouteName }
    ): NavigationSubMenuItem | null => {
      const submenuItems: SubMenuItem[] = [];

      if (backOfficeEnabled.value) {
        submenuItems.push({
          id: `${prefix}-back-office`,
          type: "button",
          routeName: routes.backOffice,
          text: i18n.global.t("general.backOffice"),
        });
      }

      if (socTicketLogEnabled.value) {
        submenuItems.push({
          id: `${prefix}-soc-tickets`,
          type: "button",
          routeName: routes.socTickets,
          text: i18n.global.t("general.socTicketLog"),
        });
      }

      if (globalWorkspacesButtonEnabled.value) {
        submenuItems.push({
          id: `${prefix}-global-workspaces`,
          type: "button",
          routeName: routes.globalWorkspaces,
          text: i18n.global.t("general.manageWorkspaces"),
        });
      }

      if (!submenuItems.length) {
        return null;
      }

      return {
        id: `${prefix}-global-menu`,
        type: "submenu",
        icon: "$dots",
        text: i18n.global.t("general.more"),
        items: submenuItems,
      };
    };

    const createLinkItem = (
      id: string,
      routeName: RouteName,
      icon: string,
      text: string
    ): NavigationLinkItem => ({ id, type: "link", routeName, icon, text });

    const buildWorkspaceMenu = (): NavigationItem[] => {
      const items: NavigationItem[] = [];

      // Keep dashboard first to anchor navigation muscle memory.
      items.push(
        createLinkItem(
          "workspace-dashboard",
          RouteName.DASHBOARD_PAGE,
          "$dashboard",
          i18n.global.t("general.home")
        ),
        createLinkItem(
          "workspace-tickets",
          RouteName.TICKETS,
          "$tickets3Xs",
          i18n.global.t("general.events")
        )
      );

      if (securityGapsButtonEnabled.value) {
        items.push(
          createLinkItem(
            "workspace-security-gaps",
            RouteName.SECURITY_GAPS_PAGE,
            "$protectionIssueXs",
            i18n.global.t("dashboard.tabs.securityGaps")
          )
        );
      }

      items.push(
        createLinkItem(
          "workspace-coro-impact",
          RouteName.CORO_IMPACT_PAGE,
          "$insightsXs",
          i18n.global.t("navigationDrawer.coroInsights")
        ),
        createLinkItem(
          "workspace-settings",
          RouteName.SETTINGS,
          "$settingsXs",
          i18n.global.t("general.controlPanel")
        )
      );

      const workspaceGlobalSubmenu = buildGlobalSubmenu("workspace", {
        backOffice: RouteName.BACK_OFFICE_PAGE,
        socTickets: RouteName.SOC_TICKETS,
        globalWorkspaces: RouteName.GLOBAL_WORKSPACE_MANAGEMENT_PAGE,
      });

      if (workspaceGlobalSubmenu) {
        items.push(workspaceGlobalSubmenu);
      }

      return items;
    };

    const buildMspMenu = (): NavigationItem[] => {
      const items: NavigationItem[] = [];

      // MSP navigation mirrors workspace order so the drawer feels familiar.
      items.push(
        createLinkItem(
          "msp-dashboard",
          RouteName.MSP_DASHBOARD_PAGE,
          "$dashboard",
          i18n.global.t("general.home")
        ),
        createLinkItem(
          "msp-overview",
          RouteName.MSP_WORKSPACES_PAGE,
          "$manageWorkspaces",
          i18n.global.t("manageWorkspaces.title")
        )
      );

      if (mspTicketsButtonEnabled.value) {
        items.push(
          createLinkItem(
            "msp-tickets",
            RouteName.MSP_TICKETS_PAGE,
            "$tickets3Xs",
            i18n.global.t("general.events")
          )
        );
      }

      if (securityGapsButtonEnabled.value) {
        items.push(
          createLinkItem(
            "msp-security-gaps",
            RouteName.MSP_SECURITY_GAPS_PAGE,
            "$protectionIssueXs",
            i18n.global.t("dashboard.tabs.securityGaps")
          )
        );
      }

      items.push(
        createLinkItem(
          "msp-coro-impact",
          RouteName.MSP_CORO_IMPACT_PAGE,
          "$insightsXs",
          i18n.global.t("navigationDrawer.coroInsights")
        )
      );

      if (showGlobalSettings()) {
        items.push(
          createLinkItem(
            "msp-control-panel",
            RouteName.MSP_CONTROL_PANEL_PAGE,
            "$settingsXs",
            i18n.global.t("manageWorkspaces.controlPanel.title")
          )
        );
      }

      const mspGlobalSubmenu = buildGlobalSubmenu("msp", {
        backOffice: RouteName.MSP_BACK_OFFICE_PAGE,
        socTickets: RouteName.MSP_SOC_TICKETS,
        globalWorkspaces: RouteName.MSP_GLOBAL_WORKSPACE_MANAGEMENT_PAGE,
      });

      if (mspGlobalSubmenu) {
        items.push(mspGlobalSubmenu);
      }

      return items;
    };

    const menuButtons = computed<NavigationItem[]>(() =>
      props.isMspRoute ? buildMspMenu() : buildWorkspaceMenu()
    );

    const userContextMenu = computed<ContextMenuItem[]>(() => {
      const logoutItem: ContextMenuItem = {
        type: "callback",
        titleKey: "appBar.userContextMenu.logout",
        callback: async () => {
          trackGtmEvent(
            GtmAction.NAVIGATION_DRAWER,
            i18n.global.t("appBar.userContextMenu.logout")
          );
          await accountStore.logout();
        },
      };

      const items: ContextMenuItem[] = [logoutItem];

      if (workspace.value.workspaceId) {
        const supportItems: ContextMenuItem[] = workspace.value.supportEnabled
          ? [
              {
                type: "callback",
                titleKey: "appBar.userContextMenu.contactSupport",
                callback: () => {
                  trackGtmEvent(
                    GtmAction.NAVIGATION_DRAWER,
                    i18n.global.t("appBar.userContextMenu.contactSupport")
                  );
                  window.open("https://docs.coro.net/", "_blank");
                },
              },
            ]
          : [];

        if (!isMFASetupInProgress.value) {
          items.unshift(
            {
              type: "link",
              titleKey: "appBar.userContextMenu.myAccount",
              routeName: RouteName.MY_ACCOUNT_PAGE,
            },
            ...supportItems,
            { type: "divider" }
          );
        } else {
          items.unshift(...supportItems, { type: "divider" });
        }
      }

      return items;
    });

    const navigateToMenuItem = async (routeName: RouteName, trackingLabel: string) => {
      await router.push({ name: routeName });
      trackGtmEvent(GtmAction.NAVIGATION_DRAWER, trackingLabel);
    };

    const handleLinkContextMenuItemNavigation = async (menuButton: ContextMenuItem) => {
      if (menuButton.type === "link") {
        await navigateToMenuItem(menuButton.routeName, i18n.global.t(menuButton.titleKey));
      }
    };

    const handleLogoClick = async () => {
      const routeToNavigate = props.isMspRoute
        ? RouteName.MSP_DASHBOARD_PAGE
        : RouteName.DASHBOARD_PAGE;
      await router.push({ name: routeToNavigate });
      trackGtmEvent(GtmAction.NAVIGATION_DRAWER, GTMParams.LOGO);
    };

    const handleListsMenu = (event: boolean) => {
      menuOpened.value = event;
      if (event) {
        trackGtmEvent(GtmAction.NAVIGATION_DRAWER, GTMParams.MORE_MENU);
      }
    };

    const isRouteActive = (routeName: RouteName) => {
      const targetRoute = router.resolve({ name: routeName });
      return route.fullPath.startsWith(targetRoute.fullPath);
    };

    const navigateToMessages = async () => {
      await router.push({ name: RouteName.ALERTS });
      trackGtmEvent(GtmAction.NAVIGATION_DRAWER, GTMParams.MESSAGES);
    };

    const isNavigationItemActive = (item: NavigationItem) => {
      if (item.type === "link") {
        return isRouteActive(item.routeName);
      }

      if (item.type === "submenu") {
        return item.items.some(
          (entry) => entry.type === "button" && isRouteActive(entry.routeName)
        );
      }

      return false;
    };

    return {
      appLogo,
      workspace,
      drawerColor,
      activeIconWrapperColor,
      menuOpened,
      nameInfo,
      initials,
      logoAlt,
      menuButtons,
      userContextMenu,
      RouteName,
      showBrandedIcon,
      navigateToMenuItem,
      handleLogoClick,
      handleLinkContextMenuItemNavigation,
      handleListsMenu,
      isNavigationItemActive,
      isRouteActive,
      navigateToMessages,
      showSetupHubItem,
      restoreOnboarding,
    };
  },
});
</script>

<style lang="scss" scoped>
.icon.menu-icon:before {
  color: white;
}

.icon-wrapper {
  padding: 4px;
  border-radius: 8px;
  margin-bottom: 4px;
}

.menu-button--active {
  opacity: 1;

  .icon-wrapper {
    background: v-bind(activeIconWrapperColor);
  }
}

.small-text {
  color: rgb(var(--v-theme-white));
  text-align: center;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 110%; /* 12.1px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  word-break: break-word;
  hyphens: auto;

  line-clamp: 2;
  -webkit-line-clamp: 2;
}

:deep(.v-list:focus-visible) {
  outline: 2px solid rgb(var(--v-theme-white));
  outline-offset: 2px;
}
</style>
